import { Component, Watch, Prop, Emit } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { showErrors } from '@/utils/snack'
import { ApolloResponse } from '@/interfaces/apollo'
import { Case } from '@/modules/Newcase/interfaces/case'

import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppTextSearchOrInput from '@/components/AppTextSearchOrInput.vue'
import TextFoldingArea from '@/modules/Newcase/components/TextFoldingArea.vue'
import AccountForm from '@/modules/Account/components/AccountForm.vue'
import AccountDialog from '@/modules/Account/scripts/account-dialog'

import { Account } from '@/modules/Account/interfaces/account'
import SearchAccount from '@/mixins/search-queries/search-account'
import RecordTypesHandler from '@/mixins/record-types'
import { getCaseById, updateNewCaseFromProducerAssignee, updateCaseClient } from '@/modules/Newcase/gql/case.gql'
import { RecordTypeMaster } from '@/modules/RecordType/interfaces/record-type'
import { findCandidateInsureds } from '@/modules/Account/gql/account.gql'
import S3Handler from '@/mixins/s3'

@Component({
  components: {
    AppBreadcrumbs,
    AppSelector,
    AppTextSearchOrInput,
    TextFoldingArea,
    AccountForm
  }
})

export default class FormSiteCreateCase extends mixins(SearchAccount, AccountDialog, RecordTypesHandler, S3Handler) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Case'
    },
    {
      text: 'Case2.0',
      to: { name: 'NewcaseDashboard' }
    },
    {
      text: 'Create Case'
    }
  ]

  account: Account | null = null
  caseInfo: Case | null = null
  typeIndex = -1
  showCreateBtn = true
  accountId = -1
  relativeAccountCandidates = []

  get accountName () {
    return this.account ? this.account.name : null
  }

  get filterTypes () {
    return this.recordTypes
  }

  get options () {
    return this.filterTypes.map(recordType => recordType.name)
  }

  get formsite () {
    if (!this.caseInfo) {
      return null
    }
    if (!this.caseInfo.formsite) {
      return null
    }
    const formsite = JSON.parse(this.caseInfo.formsite)
    return formsite
  }

  get ifNewProducer () {
    if (!this.caseInfo) {
      return true
    }
    return this.caseInfo.ifNewProducer
  }

  get producerForm () {
    if (!this.caseInfo) {
      return null
    }
    if (!this.caseInfo.producerForm) {
      return null
    }
    const producerForm = JSON.parse(this.caseInfo.producerForm)
    if (producerForm.lineOfProduct.includes('Auto')) {
      delete producerForm.content['Property Info']
    }
    if (producerForm.lineOfProduct.includes('Home')) {
      delete producerForm.content['Vehicle Info']
    }
    return producerForm
  }

  get formName () {
    if (!this.formsite && !this.producerForm) {
      return null
    }
    let name = ''
    let lineOfProduct = ''
    if (this.formsite) {
      name = this.formsite.name
      lineOfProduct = this.formsite.lineOfProduct.toLowerCase()
    }

    if (this.producerForm) {
      name = this.producerForm.name
      lineOfProduct = this.producerForm.lineOfProduct.toLowerCase()
    }
    if (name === 'Verge Auto / Home Quick Quote') {
      if (lineOfProduct === 'home') {
        return 'Verge Home Quick Quote From'
      }
      if (lineOfProduct === 'auto') {
        return 'Verge Personal Auto Quick Quote From'
      }
    }
    return name + ' From '
  }

  get headerBg () {
    if (!this.formsite && !this.producerForm) {
      return null
    }
    const name = (this.formsite || this.producerForm).name
    if (name.includes('Auto') || name.includes('Home')) {
      return '#F1663D'
    }
    return ''
  }

  get insuredName () {
    const { formsite, producerForm } = this
    if (!formsite && !producerForm) {
      return null
    }
    const insuredInfo = (formsite || producerForm).content['Insured Info']
    const name = (formsite || producerForm).insuredName
    if (insuredInfo) {
      const insuredName = insuredInfo.find((item:any) => item.key === "Insured's Name")
      return insuredName ? insuredName.value : null
    }
    if (name) {
      return name
    }
    return ''
  }

  get defaultRecordType () {
    const formName = (this.formsite || this.producerForm).name
    if (!formName) return null
    if (formName.includes('Home')) {
      return this.recordTypes.find((item) => item.name === 'Homeowners')
    }
    if (formName.includes('Auto')) {
      return this.recordTypes.find((item) => item.name === 'Personal Auto')
    }
  }

  mounted () {
    this.getCase()
    this.getRecordTypes(RecordTypeMaster.POLICY)
  }

  @Watch('account')
  onAccountChanged (val: Account | null) {
    if (!val || !val.id) {
      this.accountId = -1
    } else {
      this.accountId = val.id
    }
  }

  onCreateAccount () {
    setTimeout(() => {
      this.accountDialog.show = true
    }, 500)
  }

  onAccountName (name:string) {
    this.account = {
      id: -1,
      name
    } as Account
  }

  onCreateSuccess (accountId: number) {
    this.accountId = accountId
    this.createCaseConfirm()
  }

  handleQuickSelect (item: any) {
    this.accountId = item.id
    this.account = item
    const input = document.querySelector('.c-case-formsite__account input') as any
    if (!input) return
    input.focus()
    input.blur()
  }

  routeToProducer (id: number) {
    return {
      name: 'ProducerDetailDetail',
      params: {
        id
      }
    }
  }

  async createCaseConfirm () {
    try {
      const res = await this.$apollo.mutate({
        mutation: updateCaseClient,
        variables: {
          caseId: Number(this.$route.params.id),
          accountId: this.accountId > 0 ? +this.accountId : undefined,
          accountName: this.account ? (this.account.name || undefined) : undefined
        }
      })

      this.$router.push({
        name: 'NewcaseDetail',
        params: {
          id: this.$route.params.id
        }
      })
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async updateNewCaseFromProducerAssignee () {
    try {
      const res = await this.$apollo.mutate({
        mutation: updateNewCaseFromProducerAssignee,
        variables: {
          caseId: Number(this.$route.params.id)
        }
      })

      if (res.data) {
        this.getCase()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getCase () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCaseById,
        variables: {
          caseId: Number(this.$route.params.id)
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.caseInfo = result.data.getCaseById
      if (this.breadcrumbs.length === 2) {
        this.breadcrumbs.push({
          text: `${(this.caseInfo as Case).inquiryName} (Case ID: ${(this.caseInfo as Case).id})`
        })
      }
      this.getRelativeAccountCandidate()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getRelativeAccountCandidate () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: findCandidateInsureds,
        variables: {
          insuredName: this.insuredName
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.relativeAccountCandidates = result.data.findCandidateInsureds.slice(0, 5)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }
}
